import type {
  ConnectionEditState,
  ConnectionEditStateUpdater,
} from "../../viewmodels/board/connections/Connections.types";

export interface UpdateConnectionConfigurationOptions {
  /**
   * The editor is filling in a default it displays, not applying a user's choice. The default is
   * saved with the connection, and does not count as an unsaved change. The updater must only fill
   * missing values, because it is applied to the saved configuration as well.
   */
  isDefault?: boolean;
}

export const parseConnectionConfiguration = <
  T extends object = Record<string, unknown>,
>(
  configuration: unknown,
): Partial<T> => {
  if (!configuration) {
    return {};
  }

  if (typeof configuration === "object" && !Array.isArray(configuration)) {
    return configuration as Partial<T>;
  }

  if (typeof configuration !== "string") {
    return {};
  }

  const trimmed = configuration.trim();
  if (!trimmed) {
    return {};
  }

  try {
    const parsed = JSON.parse(trimmed) as unknown;
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as Partial<T>)
      : {};
  } catch {
    return {};
  }
};

export const updateConnectionConfiguration = <
  T extends object = Record<string, unknown>,
>(
  editingConnection: ConnectionEditState,
  onUpdate: ConnectionEditStateUpdater,
  updater: (configuration: Partial<T>) => Partial<T>,
  options: UpdateConnectionConfigurationOptions = {},
): void => {
  const currentConfiguration = parseConnectionConfiguration<T>(
    editingConnection.configuration,
  );
  const nextConfiguration = updater(currentConfiguration);
  // `editingConnection` is the form the editor last rendered, which can be older than the
  // form itself; the updater is applied again to the latest configuration.
  const updateConfiguration = (configuration: unknown): Partial<T> =>
    updater(parseConnectionConfiguration<T>(configuration));

  if (!options.isDefault) {
    onUpdate({ configuration: nextConfiguration }, { updateConfiguration });
    return;
  }

  onUpdate(
    { configuration: nextConfiguration },
    {
      isDefault: true,
      applyDefault: updateConfiguration,
      updateConfiguration,
    },
  );
};
